// packages/shared-components/src/components/Button/Button.tsx
import { forwardRef } from "react";
import { ButtonProps } from "./types";
import { Iconfont } from "../Iconfont";
import styles from "./Button.module.css";

/**
 * Button 组件
 *
 * loading 用法：
 * - 传 `loading` 或 `loading={true}` 即进入加载态
 * - 加载态会显示「loading 图标 + 原有按钮文案」
 * - 加载态按钮会自动禁用（disabled）
 * - `loading={false}` 时恢复普通按钮内容（显示 children 文案）
 *
 * 示例：
 * `<Button variant="primary" loading>保存</Button>`
 * `<Button loading={isSubmitting}>提交</Button>`
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "default",
      size = "md",
      active = false,
      loading = false,
      disabled = false,
      leftIcon,
      rightIcon,
      className = "",
      children,
      ...props
    },
    ref
  ) => {
    const classNames = [
      styles.button,
      styles[`button_${variant}`],
      styles[`button_${size}`],
      active && styles.button_active,
      loading && styles.button_loading,
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={classNames}
        {...props}
      >
        {loading ? (
          <span className={styles.loadingContent}>
            <Iconfont icon="loading" className={styles.loading} />
            {children}
          </span>
        ) : (
          <>
            {leftIcon}
            {children}
            {rightIcon}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = "Button";
